import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { OrganizationService } from '../../core/data-services/organization.service';
import { calculateBitwiseRole } from '../../core/utils/member-role-utils';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';
import { AdminMemberView } from '../../core/data-models/admin-member-view.model';

/**
 * Assign Owner Modal Component
 *
 * Modal for transferring organization ownership to another member.
 * Allows selecting new owner and optionally keeping old owner as PIC or Pilot.
 *
 * Usage:
 * ```html
 * <app-assign-owner-modal
 *   [organizationId]="orgId"
 *   [members]="membersList"
 *   [currentOwnerId]="ownerId"
 *   (close)="onModalClose()"
 *   (ownershipTransferred)="onOwnershipTransferred()">
 * </app-assign-owner-modal>
 * ```
 */
@Component({
  selector: 'app-assign-owner-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onClose()"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Transfer Organization Ownership</h5>
            <button type="button" class="btn-close" (click)="onClose()" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Error Message -->
            <div class="alert alert-danger" *ngIf="errorMessage">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div class="alert alert-success" *ngIf="successMessage">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ successMessage }}
            </div>

            <!-- Warning -->
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Warning:</strong> Transferring ownership will change the organization owner.
              This action cannot be undone through the admin portal.
            </div>

            <!-- Current Owner -->
            <div class="mb-4" *ngIf="currentOwner">
              <label class="form-label fw-bold">Current Owner</label>
              <div class="border rounded p-3 bg-body-tertiary">
                <div class="d-flex align-items-center">
                  <div class="entity-avatar fs-5 me-3">
                    <i class="bi bi-person-fill"></i>
                  </div>
                  <div>
                    <div class="fw-semibold">{{ currentOwner.firstName }} {{ currentOwner.lastName }}</div>
                    <div class="small text-body-secondary">{{ currentOwner.email }}</div>
                  </div>
                  <span class="badge bg-warning text-dark ms-auto">Owner</span>
                </div>
              </div>
            </div>

            <!-- No Eligible Members Message -->
            <div class="alert alert-info" *ngIf="eligibleMembers.length === 0">
              <i class="bi bi-info-circle-fill me-2"></i>
              <strong>No eligible members available.</strong>
              <p class="mb-0 mt-1">
                Ownership can only be transferred to members who have accepted their invitation and created an account.
                Please ensure at least one member has completed their registration before transferring ownership.
              </p>
            </div>

            <!-- New Owner Selection -->
            <div class="mb-3" *ngIf="eligibleMembers.length > 0">
              <label for="newOwner" class="form-label fw-bold">
                Select New Owner <span class="text-danger">*</span>
              </label>
              <select
                class="form-select"
                id="newOwner"
                [(ngModel)]="selectedNewOwnerId"
                [disabled]="saving"
                [class.is-invalid]="!selectedNewOwnerId && showValidation"
              >
                <option [ngValue]="null">-- Select a member --</option>
                <option
                  *ngFor="let member of eligibleMembers"
                  [value]="member.profileId"
                >
                  {{ member.firstName }} {{ member.lastName }} ({{ member.email }})
                </option>
              </select>
              <div class="invalid-feedback" *ngIf="!selectedNewOwnerId && showValidation">
                Please select a new owner
              </div>
            </div>

            <!-- Old Owner Role -->
            <div class="mb-4" *ngIf="eligibleMembers.length > 0">
              <label class="form-label fw-bold">Assign Role to Current Owner</label>
              <p class="text-muted small">
                After ownership transfer, assign a new role to the current owner.
              </p>

              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="keepPilot"
                  [(ngModel)]="keepAsPilot"
                  [disabled]="saving"
                />
                <label class="form-check-label" for="keepPilot">
                  Keep as Pilot
                </label>
              </div>

              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="keepPIC"
                  [(ngModel)]="keepAsPIC"
                  [disabled]="saving"
                />
                <label class="form-check-label" for="keepPIC">
                  Keep as Pilot in Command (PIC)
                </label>
              </div>
            </div>

            <!-- Divider -->
            <hr class="my-3" *ngIf="eligibleMembers.length > 0" />

            <!-- Confirmation Checkbox -->
            <div class="mb-3" *ngIf="eligibleMembers.length > 0 && !confirmed">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="confirmTransfer"
                  [(ngModel)]="confirmed"
                  [disabled]="saving"
                />
                <label class="form-check-label" for="confirmTransfer">
                  <strong>I understand that this will transfer ownership of the organization</strong>
                </label>
              </div>
            </div>

            <!-- Transfer Summary -->
            <div class="alert alert-info" *ngIf="selectedNewOwnerId && confirmed">
              <h6 class="alert-heading">Transfer Summary</h6>
              <div>
                <strong>New Owner:</strong> {{ getSelectedMemberName() }}
              </div>
              <div>
                <strong>Current Owner Role After Transfer:</strong>
                {{ getOldOwnerRoleName() }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onClose()" [disabled]="saving">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-warning"
              (click)="onTransfer()"
              [disabled]="saving || !selectedNewOwnerId || !confirmed || eligibleMembers.length === 0"
            >
              <span *ngIf="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ saving ? 'Transferring...' : 'Transfer Ownership' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AssignOwnerModalComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() members: AdminMemberView[] = [];
  @Input() currentOwnerId!: string;
  @Output() close = new EventEmitter<void>();
  @Output() ownershipTransferred = new EventEmitter<void>();

  selectedNewOwnerId: string | null = null;
  keepAsPilot = false;
  keepAsPIC = true; // Default to keeping as PIC
  confirmed = false;
  saving = false;
  errorMessage = '';
  successMessage = '';
  showValidation = false;

  currentOwner?: AdminMemberView;
  eligibleMembers: AdminMemberView[] = [];

  constructor(private organizationService: OrganizationService) {}

  ngOnInit(): void {
    // Find current owner
    this.currentOwner = this.members.find(m => m.profileId === this.currentOwnerId);

    // Eligible members are active members who have accepted their invite (have a Firebase account)
    // and are not the current owner. Treat missing hasAcceptedInvite as accepted for backwards compatibility.
    this.eligibleMembers = this.members.filter(m =>
      m.isActive && m.profileId !== this.currentOwnerId && m.hasAcceptedInvite !== false
    );
  }

  /**
   * Get selected member name
   */
  getSelectedMemberName(): string {
    const member = this.members.find(m => m.profileId === this.selectedNewOwnerId);
    return member ? `${member.firstName} ${member.lastName}` : '';
  }

  /**
   * Get old owner role name after transfer
   */
  getOldOwnerRoleName(): string {
    if (!this.keepAsPilot && !this.keepAsPIC) {
      return 'No role (will be removed from organization)';
    }
    const roles: string[] = [];
    if (this.keepAsPIC) roles.push('PIC');
    if (this.keepAsPilot) roles.push('Pilot');
    return roles.join(', ');
  }

  /**
   * Transfer ownership
   */
  async onTransfer(): Promise<void> {
    this.showValidation = true;
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.selectedNewOwnerId) {
      this.errorMessage = 'Please select a new owner';
      return;
    }

    if (!this.confirmed) {
      this.errorMessage = 'Please confirm the ownership transfer';
      return;
    }

    this.saving = true;

    try {
      // Calculate old owner role
      const oldOwnerRole = calculateBitwiseRole(this.keepAsPilot, this.keepAsPIC);

      await firstValueFrom(
        this.organizationService.transferOwnership(this.organizationId, this.selectedNewOwnerId, oldOwnerRole)
      );

      this.successMessage = 'Ownership transferred successfully';

      // Wait briefly to show success message, then emit event and close
      setTimeout(() => {
        this.ownershipTransferred.emit();
        this.onClose();
      }, 1500);
    } catch (error: any) {
      console.error('Failed to transfer ownership:', error);
      this.errorMessage = error.message || 'Failed to transfer ownership';
      this.saving = false;
    }
  }

  /**
   * Close modal
   */
  onClose(): void {
    this.close.emit();
  }
}
